import React from 'react';
import {
  type OpenFeatureSupportedFlags,
  type OpenFeatureValue,
} from '@mentimeter/flags/openfeature/types';
import {
  useConfidenceOverrides,
  type ConfidenceOverrideContextT,
} from '@mentimeter/flags/confidence/development-tools';
import { flagsMetadata } from '@mentimeter/flags/confidence/experiments';
import { searchFlags, sortFlags } from '../utils';
import type { FlagT } from '../types';
import { DEFAULT_VALUE } from '../constants';

export function useConfidenceFlags({
  flags,
  searchString,
}: {
  flags: OpenFeatureSupportedFlags;
  searchString: string;
}) {
  const overrides = useConfidenceOverrides();

  const displayFlags = React.useMemo(() => {
    const flattenedFlags = flattenConfidenceFlags(flags);

    return Object.keys(flattenedFlags)
      .map(fromConfidenceFlag(flattenedFlags, overrides))
      .filter(searchFlags(searchString))
      .sort(sortFlags);
  }, [flags, overrides, searchString]);

  return {
    confidenceFlags: displayFlags,
    activeConfidenceFlags: displayFlags.filter((flag) => flag.active),
    inactiveConfidenceFlags: displayFlags.filter((flag) => !flag.active),
    resetAllConfidenceFlags() {
      overrides.clearAll();
    },
    setConfidenceOverride: (key: string, value: string) => {
      overrides.set(key, value);
    },
    resetConfidenceOverride: (key: string) => {
      overrides.reset(key);
    },
  };
}

type FlatConfidenceFlags = Record<string, OpenFeatureValue>;

function fromConfidenceFlag(
  flattenedFlags: FlatConfidenceFlags,
  overrides: ConfidenceOverrideContextT,
): (flagName: string) => FlagT {
  return (flagName: string) => {
    const treatments = flattenedFlags[flagName]?.map((treatment) =>
      String(treatment),
    );
    const value = overrides.values[flagName] ?? DEFAULT_VALUE;
    const displayName = isConfidenceMetadataFlagKey(flagName)
      ? flagsMetadata[flagName].displayName
      : flagName;
    const description = isConfidenceMetadataFlagKey(flagName)
      ? flagsMetadata[flagName].description
      : undefined;
    const externalLink = getConfidenceFlagLink(flagName);

    return {
      key: flagName,
      displayKey: flagName,
      displayName,
      description,
      type: 'confidence',
      options: treatments,
      value,
      active: value !== undefined,
      externalLink,
    };
  };
}

function flattenConfidenceFlags(
  flags: OpenFeatureSupportedFlags,
): FlatConfidenceFlags {
  const result: Record<string, OpenFeatureValue> = {};

  Object.keys(flags).forEach((baseFlagKey) => {
    const configs = flags[baseFlagKey];

    if (configs === undefined) return;

    Object.keys(configs).forEach((configKey) => {
      const flagKey = `${baseFlagKey}.${configKey}`;
      const flagValue = configs[configKey];

      if (flagValue === undefined) return;

      result[flagKey] = flagValue;
    });
  });

  return result;
}

function getConfidenceFlagLink(flagKey: string) {
  return 'https://app.confidence.spotify.com/flags/' + flagKey.split('.')[0];
}

function isConfidenceMetadataFlagKey(
  key: string,
): key is keyof typeof flagsMetadata {
  return key in flagsMetadata;
}
